<div class="px-3 pb-8" data-cy="network-migration-page">
  @if (contextError()) {
    <app-notification-block
      data-cy="network-migration-workflow-context-error"
      [header]="'Unable to load Network Migration'"
      [statusClass]="StatusClass.ERROR">
      {{ contextError() }}
    </app-notification-block>
  }
  <mat-card
    class="workflow-banner mb-4 rounded-[1rem] border border-[#d7e0ee] bg-white px-4 py-4 shadow-[0_2px_10px_rgba(15,23,42,0.06)]"
    data-cy="migration-workflow-banner">
    <div class="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
      <div class="flex min-w-0 flex-wrap items-center gap-2 text-[0.875rem] text-slate-600">
        <button
          type="button"
          class="inline-flex items-center gap-[0.45rem] border-0 bg-transparent p-0 text-sm font-medium text-slate-600 cursor-pointer"
          data-cy="migration-workflow-back"
          (click)="goBackToNetworkMigration()">
          <span class="icon back-icon"></span>
          <span>Network Migration</span>
        </button>

        <span class="w-px h-4 bg-slate-200" aria-hidden="true"></span>
        <span class="icon icon-building"></span>
        <span class="font-semibold text-slate-900">{{ workflowBuildingName() }}</span>

        @if (buildingId()) {
          <span class="inline-flex items-center min-h-[1.85rem] rounded-full  text-slate-400 font-medium">{{ buildingId() }}</span>
        }
        @if (workflowCustomer()) {
          <span class="inline-flex items-center min-h-[1.85rem] rounded-full bg-slate-50 px-3 text-[0.8125rem] text-slate-500 font-medium">{{ workflowCustomer() }}</span>
        }

        <span [class]="'status-pill status-pill--' + bannerStatus().toLowerCase()">
          <span class="status-pill-dot" aria-hidden="true"></span>
          {{ bannerStatusLabel() }}
        </span>

        @if (bannerUpdatedAt()) {
          <span class="text-slate-400 text-[0.8125rem] font-medium">Updated {{ bannerUpdatedAt() }}</span>
        }
      </div>
    </div>
  </mat-card>

  <mat-card
    class="network-migration-summary rounded-[1.25rem] border border-[#d7e0ee] bg-white px-5 py-6 shadow-[0_2px_10px_rgba(15,23,42,0.06)]">
    <div class="grid grid-cols-1 items-center gap-4  md:grid-cols-2 xl:grid-cols-[112px_repeat(4,minmax(170px,1fr))]">
      <div class="relative mx-auto grid h-30 w-30 place-items-center md:col-span-2 xl:col-span-1">
        <div class="migration-progress-ring relative h-29 w-29" aria-hidden="true">
          <mat-progress-spinner
            class="migration-progress-value absolute inset-0"
            mode="determinate"
            [diameter]="116"
            [strokeWidth]="8"
            [value]="migrationCompletion()"
          />
        </div>
        <div class="absolute grid justify-items-center gap-px">
          <span class="text-sm leading-none font-medium text-slate-800">{{ migrationCompletion() }}%</span>
          <span class="text-xs leading-none font-medium tracking-[0.02em] text-slate-500">COMPLETE</span>
        </div>
      </div>

      <mat-card
        class="container grid min-h-20 place-content-center gap-0.5 rounded-[0.95rem] border-none px-3 py-1.5 text-center shadow-none">
        <span class="text-2xl leading-none font-medium text-slate-800">{{ totalNodes() }}</span>
        <span class="text-sm leading-[1.2] font-medium text-slate-500">Total Nodes</span>
      </mat-card>

      <mat-card
        class="grid min-h-20 place-content-center gap-0.5 rounded-[0.95rem] border-none migrated-card px-3 py-1.5 text-center shadow-none">
        <span class="text-2xl leading-none font-medium text-[#22c55e]">{{ migratedNodes() }}</span>
        <span class="text-sm leading-[1.2] font-medium text-slate-500">Migrated</span>
      </mat-card>

      <mat-card
        class="grid min-h-20 place-content-center gap-0.5 rounded-[0.95rem] border-none  pending-card px-3 py-1.5 text-center shadow-none">
        <span class="text-2xl leading-none font-medium text-[#3b82f6]">{{ pendingNodes() }}</span>
        <span class="text-sm leading-[1.2] font-medium text-slate-500">Pending</span>
      </mat-card>

      <mat-card
        class="grid min-h-20 place-content-center gap-0.5 rounded-[0.95rem] border-none failed-card px-3 py-1.5 text-center shadow-none">
        <span class="text-2xl leading-none font-medium text-red-700">{{ failedNodes() }}</span>
        <span class="text-sm leading-[1.2] font-medium text-slate-500">Failed</span>
      </mat-card>
    </div>

    <div class="mt-4 h-2 w-full overflow-hidden rounded-full bg-slate-200" aria-hidden="true">
      <div
        [class]="'migration-progress-fill migration-progress-fill--' + progressTone()"
        [style.width.%]="migrationCompletion()"
      ></div>
    </div>
  </mat-card>

  <mat-card
    class="migration-workflow-steps mt-4 rounded-[1.25rem] border border-[#d7e0ee] bg-white px-4 py-6 shadow-[0_2px_10px_rgba(15,23,42,0.06)]"
    data-cy="network-migration-workflow-card">
    <div class="mb-3 flex items-center justify-between gap-3">
      <div class="flex items-center gap-2">
        <span class="icon migration-icon"></span>
        <span class="text-base leading-[1.2] font-medium text-slate-800">Migration Workflow</span>
        <span class="text-sm leading-[1.2] font-medium text-slate-400">
          {{ workflowCompletedSteps() }}/{{ workflowTotalSteps }} steps
        </span>
      </div>
      <button
        type="button"
        class="text-sm leading-[1.2] font-medium text-slate-400"
        data-cy="network-migration-collapse-all"
        (click)="toggleWorkflowSteps()">
        {{ workflowExpansionActionLabel() }}
      </button>
    </div>

    @for (step of workflowSteps(); track step.id) {
      <mat-expansion-panel
                           class="workflow-step-panel workflow-step-row overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-none"
        [expanded]="expandedWorkflowStepIds().has(step.id)"
        (opened)="setWorkflowStepExpanded(step.id, true)"
        (closed)="setWorkflowStepExpanded(step.id, false)"
        [attr.data-cy]="'network-migration-step-' + step.id">
        <span
          class="absolute inset-y-0 left-0 w-1"
          [class.bg-emerald-500]="step.status === BLEKeyDeliveryStatus.Completed"
          [class.workflow-step-status--failed]="step.status === BLEKeyDeliveryStatus.Failed || step.status === BLEKeyDeliveryStatus.ActionRequired"
          [class.bg-slate-200]="step.status === BLEKeyDeliveryStatus.Pending ||
              step.status === BLEKeyDeliveryStatus.NoOpenJoiningSession ||
              step.status === BLEKeyDeliveryStatus.WaitingForOpenJoiningToFinish ||
              step.status === BLEKeyDeliveryStatus.Cancelled"
          [class.bg-blue-400]="step.status === BLEKeyDeliveryStatus.InProgress"
          aria-hidden="true"></span>

        <mat-expansion-panel-header
          class="workflow-step-header px-4 py-3"
          [attr.data-cy]="'network-migration-step-toggle-' + step.id">
          <span
            class="absolute inset-y-0 left-0 w-1"
            [class.bg-emerald-500]="step.status === BLEKeyDeliveryStatus.Completed"
            [class.workflow-step-status--failed]="step.status === BLEKeyDeliveryStatus.Failed || step.status === BLEKeyDeliveryStatus.ActionRequired"
            [class.bg-slate-200]="step.status === BLEKeyDeliveryStatus.Pending ||
              step.status === BLEKeyDeliveryStatus.NoOpenJoiningSession ||
              step.status === BLEKeyDeliveryStatus.WaitingForOpenJoiningToFinish ||
              step.status === BLEKeyDeliveryStatus.Cancelled"
            [class.bg-blue-400]="step.status === BLEKeyDeliveryStatus.InProgress"
            aria-hidden="true"></span>
          <div class="flex min-w-0 flex-1 items-center justify-between gap-4 text-left m-3">
            <div class="flex min-w-0 items-center gap-3">
              <span class="workflow-step-badge workflow-step-badge--number ml-4">{{ step.id }}</span>
                <span
                  class="inline-flex w-4 h-4 shrink-0 bg-no-repeat bg-center bg-contain"
                  [class.node-status-icon--joined]="step.status === BLEKeyDeliveryStatus.Completed"
                  [class.node-status-icon--failed]="step.status === BLEKeyDeliveryStatus.Failed || step.status === BLEKeyDeliveryStatus.ActionRequired"
                  [class.node-status-icon--attempting]="step.status === BLEKeyDeliveryStatus.InProgress"
                  [class.node-status-icon--pending]="step.status === BLEKeyDeliveryStatus.Pending"
                ></span>
              <span class="truncate text-base leading-[1.2] font-medium text-slate-800">{{ step.title }}</span>
            </div>

            <div class="flex shrink-0 items-center gap-3">
             @if(bleKeyDeliveryResponse().totalNodes > 0) {
                <button
                  mat-icon-button
                  type="button"
                  class="copy-nodes-button rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-500"
                  [matTooltip]="'Copy nodes to clipboard'"
                  data-cy="ble-delivery-log-copy-nodes"
                  (click)="$event.stopPropagation(); copyNodesToClipboard()">
                  <span class='icon copy-icon'></span>
                </button>
             }
              @if(retriesLabel()) {
                <span class="flex items-center gap-2 whitespace-nowrap pr-2">
                  <span class="icon icon-retry" aria-hidden="true"></span>
                  <span class="text-sm leading-[1.2] font-medium text-amber-600">{{ retriesLabel() }}</span>
                </span>
              }
              @if(allowRetry()) {
                <div class="p-2">
                  <button
                    mat-flat-button
                    type="button"
                    class="workflow-retry-btn ble-delivery-log-retry "
                    [attr.data-cy]="'ble-key-step-retry'"
                    (click)="$event.stopPropagation(); retryBleKeyDelivery()">
                      <span class="inline-flex items-center gap-[0.32rem] leading-none">
                        <span class="icon icon-retry-btn" aria-hidden="true"></span>
                        <span>Retry</span>
                      </span>
                  </button>
                </div>
              }
            </div>
          </div>
        </mat-expansion-panel-header>
        @if (bleKeyDeliveryError()) {
          <section class="status-panel text-slate-700 bg-white status-panel-error mt-2" data-cy="network-migration-workflow-ble-error">
            {{ bleKeyDeliveryError() }}
          </section>
        }
        <div class="space-y-2 py-2" data-cy="ble-delivery-log">
          @for (attempt of step.attempts ?? []; track $index) {
            <mat-card
              class="ble-delivery-log"
              [class.fail-card]="attempt.status === BLEKeyDeliveryStepStatus.Failed || attempt.status === BLEKeyDeliveryStepStatus.Skipped"
              [class.success-card]="attempt.status === BLEKeyDeliveryStepStatus.Completed"
              [class.info-card]="attempt.status === BLEKeyDeliveryStepStatus.InProgress || attempt.status === BLEKeyDeliveryStepStatus.Pending"
              appearance="outlined">
              <div class="p-2">
                <p class="ble-delivery-log-line font-semibold text-slate-500">
                  <span class="mr-2">[INFO]</span>
                  BLE attempted delivery to {{ attempt.totalNodes }} nodes
                </p>
                <p class="ble-delivery-log-line font-semibold text-slate-500">
                  <span class="mr-2">[INFO]</span>
                  {{ attempt.completedNodes }}/{{ attempt.totalNodes }} nodes acknowledged new BLE keys
                </p>
                @if (attempt.failedNodes) {
                  <p class="ble-delivery-log-line ble-delivery-log-line--error font-semibold">
                    <span class="mr-2">[ERROR]</span>
                    {{ attempt.message }}
                  </p>
                }
                @if (attempt.pendingNodes) {
                  <p class="ble-delivery-log-line ble-delivery-log-line--running font-semibold">
                    <span class="mr-2">[RUNNING]</span>
                    Awaiting acknowledgement from {{ attempt.pendingNodes }} nodes…
                  </p>
                }
              </div>
            </mat-card>
          }
        </div>
      </mat-expansion-panel>
    }
  </mat-card>
</div>
